import { useEffect, useMemo, useState } from 'react';

import { useInView } from 'react-intersection-observer';

import { useAppSelector } from 'dux/app/hooks';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';
import { makeStyles } from 'legacyStyles';

import { Pricing } from 'Components/Pricing';
import { breakpoints } from 'Components/ThemeProvider';
import { Typography } from 'Components/Typography';
import WaitingListForm from 'Components/WaitingListForm';

import currencies from 'constants/currencies';
import type { productsCategories } from 'constants/products';

import SliderAndContent from 'Blocks/SliderAndContent';

import ProductCTA from 'Containers/ProductCTA';

import { srcOrNextImageProp } from 'PropTypes/imageProps';

import useMedia from 'utils/useMedia';

import { getHaveOrders, getUserEmail } from 'dux/user/selectors';
import { useFetchProductsList } from 'dux/products/hooks';

const Description = styled(Typography)`
  margin: 0;
`;

const SeparatorLine = styled.div`
  width: 30px;
  height: 1px;
  margin: ${theme.spacing['6x']} auto;
  background-color: ${theme.colors.neutral[400]};
`;

const StyledDisclaimer = styled(Typography)`
  margin-top: ${theme.spacing['4x']};
  color: ${theme.colors.neutral[800]};
`;

const StyledCapacity = styled(Typography)`
  margin: ${theme.spacing['6x']};
`;

const useStyles = makeStyles(
  {
    button: {
      marginBottom: legacyTheme.spacing.s24,
      marginTop: 0,
      [legacyTheme.breakpoints.up('sm')]: {
        marginBottom: legacyTheme.spacing.s32,
        marginTop: legacyTheme.spacing.s8,
      },
    },
  },
  { name: 'ProductPageHero' },
);

type ProductPageHeroProps = {
  useShortLabel?: boolean;
  content: {
    imageCarousel: { image: typeof srcOrNextImageProp; alt: string }[];
    guarantee: React.ReactNode;
    soldOutNotice: string;
    waitingListNotice: string;
    product: {
      category: typeof productsCategories.HAIRCARE | typeof productsCategories.SKINCARE;
      capacity: string;
      currency: string;
      description: React.ReactNode;
      shortLabel: string;
      label: string;
      price: number;
      priceWithSubscription: number;
      displayLabelPdp: string;
      displayLabel: React.ReactNode;
      regularPrice: number;
      slug: string;
      tag: string;
      keyBenefits: React.ReactNode;
      disclaimer: React.ReactNode;
    };
  };

  noShippingLabel?: string;
};

export const ProductPageHero = ({
  content,
  useShortLabel = false,
  noShippingLabel = '',
}: ProductPageHeroProps) => {
  const hasOrder = useAppSelector(getHaveOrders);
  const userEmail = useAppSelector(getUserEmail);
  const { isLoaded } = useFetchProductsList();
  // @Marc: initialized to true because the refered block is initially visible so we don't want the sticky CTA to be shown
  const [ref, inView] = useInView({ initialInView: true });
  const isMobile = useMedia(`(max-width: ${breakpoints.sm}px)`);
  const fixedSubmit = useMemo(() => isMobile && !inView, [isMobile, inView]);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const { classes } = useStyles();

  const { product } = content;
  const label = useShortLabel ? product.shortLabel : product.label;
  const displayLabel = product.displayLabel ?? label;
  const showWaitingList = Boolean(content.soldOutNotice);

  useEffect(() => {
    // @Benoit: reset the carousel index after going on a new pdp
    setCarouselIndex(0);
  }, [content?.product?.slug]);

  return (
    // @ts-expect-error - SliderAndContent has no TS types for now
    <SliderAndContent
      ref={ref}
      backgroundColor="beige"
      blockLayoutMargin={isMobile ? '70px auto 40px' : '90px auto'}
      carouselIndex={carouselIndex}
      onIndexChange={setCarouselIndex}
      sliderDataFrom="product-module"
      slides={content.imageCarousel}
    >
      <Typography align="center" color="grey" markupName="p" paragraph variant="mono3">
        {product.tag}
      </Typography>

      <Typography align="center" data-testid={label} markupName="h1" paragraph variant="h1r">
        {product.displayLabelPdp ?? `Custom ${displayLabel}`}
      </Typography>

      <Description align="center" markupName="p" paragraph variant="p2">
        {product.description}
        {noShippingLabel ? '†' : ''}
      </Description>
      <SeparatorLine />

      <Typography align="center" markupName="p" variant="p2">
        {product.keyBenefits}
      </Typography>
      {product.disclaimer && (
        <StyledDisclaimer align="center" markupName="p" variant="p3">
          {product.disclaimer}
        </StyledDisclaimer>
      )}

      <StyledCapacity align="center" paragraph variant="p2">
        {product.capacity}
      </StyledCapacity>
      {isLoaded && (
        <Pricing
          currency={product.currency ?? currencies.USD}
          price={product.regularPrice || product.price}
          priceWithSubscription={product.priceWithSubscription}
          productCategory={product.category}
          showDiscountModal
        />
      )}
      <ProductCTA
        classes={{ root: classes.button }}
        data-testid={`${product.slug}-pdp-cta`}
        dataFrom="product-module"
        fullWidth
        productCategory={product.category}
        productName={label}
        render={
          showWaitingList ? (
            <WaitingListForm
              description={`${content.soldOutNotice} ${content.waitingListNotice}`}
              fixedSubmit={fixedSubmit}
              initialEmail={userEmail}
              listId={815007}
              slug={product.slug}
            />
          ) : null
        }
        slug={product.slug}
        theme="vert"
      />
      {!showWaitingList && isMobile && !inView && (
        <ProductCTA
          dataFrom="product-module-sticky"
          fixed
          productCategory={product.category}
          productName={label}
          slug={product.slug}
          theme="vert"
        />
      )}
      {content.guarantee && !hasOrder && (
        <Typography align="center" color="grey" paragraph variant="p2">
          {content.guarantee}
        </Typography>
      )}
      {noShippingLabel && (
        <Typography align="center" color="grey" paragraph variant="p2">
          {noShippingLabel}
        </Typography>
      )}
    </SliderAndContent>
  );
};
